import axios from 'axios';
import SERVER_CONFIG from './config';

const API_URL = SERVER_CONFIG.apiUrl;

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add token to requests
api.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

console.log('[API] Инициализирован с сервером:', API_URL);

// Auth
export const login = async (username, password) => {
  const response = await api.post('/auth/login', { username, password });
  return response.data;
};

export const getCurrentUser = async () => {
  const response = await api.get('/auth/profile');
  return response.data;
};

// Avatar
export const uploadAvatar = async (formData) => {
  const response = await api.post('/auth/avatar', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

export const deleteAvatar = async () => {
  const response = await api.delete('/auth/avatar');
  return response.data;
};

// Messages
export const getMessages = async (channelId, limit = 100, before = null) => {
  const params = { limit };
  if (before) params.before = before;
  const response = await api.get(`/channels/${channelId}/messages`, { params });
  return response.data.messages;
};

// Messages with read status (умная загрузка)
export const getMessagesWithReadStatus = async (channelId, limit = 100) => {
  const params = { withReadStatus: true, limit };
  const response = await api.get(`/channels/${channelId}/messages`, { params });
  return response.data; // { messages, firstUnreadMessageId, lastReadMessageId }
};

// Получить старые сообщения для динамической подгрузки
export const getOlderMessages = async (channelId, beforeMessageId, limit = 20) => {
  const params = { before: beforeMessageId, limit };
  const response = await api.get(`/channels/${channelId}/messages`, { params });
  return response.data.messages;
};

// Все сообщения из доступных каналов
export const getAllMessages = async (limit = 50) => {
  const response = await api.get('/channels/all/messages', { params: { limit } });
  return response.data.messages;
};

export const getOlderAllMessages = async (beforeMessageId, limit = 50) => {
  const response = await api.get('/channels/all/messages', { params: { before: beforeMessageId, limit } });
  return response.data.messages;
};

// Отметить сообщения как прочитанные
export const markMessagesAsRead = async (channelId, lastReadMessageId) => {
  await api.post(`/channels/${channelId}/mark-read`, { lastReadMessageId });
};

// Получить счетчики непрочитанных сообщений
export const getUnreadCounts = async () => {
  const response = await api.get('/channels/unread-counts');
  return response.data.counts;
};

// Files
export const uploadFile = async (channelId, formDataOrFile, content = '', onUploadProgress = null) => {
  let formData;

  // Check if already FormData (for multiple files)
  if (formDataOrFile instanceof FormData) {
    formData = formDataOrFile;
  } else {
    // Single file - create FormData
    formData = new FormData();
    formData.append('files', formDataOrFile);
    formData.append('channelId', channelId);
    if (content) formData.append('content', content);
  }

  // Автоповтор при сетевых обрывах (частые на мобильной связи) и временных ошибках шлюза.
  // НЕ повторяем при 4xx-ответах сервера (кроме 408) — это не транзиентный сбой.
  const MAX_ATTEMPTS = 3;
  let lastErr;
  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
    try {
      const response = await api.post('/files/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 30 * 60 * 1000,
        onUploadProgress: onUploadProgress || undefined
      });
      return response.data;
    } catch (err) {
      lastErr = err;
      const status = err.response && err.response.status;
      const retriable = !status || [408, 502, 503, 504].includes(status);
      if (!retriable || attempt === MAX_ATTEMPTS) break;
      await new Promise(r => setTimeout(r, 1500 * attempt));
    }
  }
  throw lastErr;
};

export const getFileUrl = (fileId) => {
  const token = localStorage.getItem('token');
  const baseUrl = SERVER_CONFIG.socketUrl;
  return `${baseUrl}/api/files/${fileId}?token=${token}`;
};

export const toggleFileProtection = async (fileId) => {
  const response = await api.post(`/files/${fileId}/toggle-protection`);
  return response.data;
};

// Push notifications
export const getPushPublicKey = async () => {
  const response = await api.get('/push/public-key');
  return response.data.publicKey;
};

export const subscribeToPush = async (subscription) => {
  await api.post('/push/subscribe', { subscription });
};

export const unsubscribeFromPush = async (endpoint) => {
  await api.post('/push/unsubscribe', { endpoint });
};

export const deleteMessage = async (messageId) => {
  const response = await api.delete(`/messages/${messageId}`);
  return response.data;
};

export default api;
